import React from "react";
import { Box, FormHelperText, styled, useTheme } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircleOutlineOutlined } from "@mui/icons-material";

import { BareAxios, enterInput, hideGrecaptcha, mountGrecaptcha, onlyEmptyKeys, svgGradient } from "../../common/utils";
import { baseApi, FIFTEEN_MINUTES } from "../../common/constants";
import { accountInputBoxCSS, AppLoadingButton, LoginButton, SignInCard } from "../subComps/loginPieces";
import { fieldRules, LiLink } from "../../common/commonComps";
import { AccountInputBox } from "../account/components/TableActions";
import { Span } from "../account/components/Typography";
import OTPInput from "../subComps/otp";
import type { AppContext, FieldDescriptor, Size } from "../../types";
import { base91decode, base91encode } from "../../common/base91";

declare const grecaptcha: { execute: (key: string, options: { action: string }) => Promise<string> };

const validationSchemaLogIn = Yup.object().shape({
    firstName: Yup.string().required(`Name and Email are required`).min(2, `Name and Email are required`),
    lastName: Yup.string().required(`Name and Email are required`).min(2, `Name and Email are required`),
    email: Yup.string().email(`Please enter a valid email`).max(255).required(`Name and Email are required`),
    rwCode: Yup.string().required(`Please enter your reward code`),
});

const GreyLine = styled(Box)({
    background: `#f0f0f0`,
    borderRadius: `50px`,
    margin: `0 auto 20px`,
    padding: `3px 8px`,
    display: `flex`,
});

const LoginInputBox = styled(AccountInputBox)(accountInputBoxCSS);

const codeValidationSchema = Yup.string()
    .required(`Code is required`)
    .length(4, `The code is not valid`)
    .matches(/^[A-Z0-9]+$/, `The code is not valid`);

interface GetMyGiftLoginProps {
    context: AppContext;
    size: Size;
}

const GetMyGiftLogin = ({ context, size }: GetMyGiftLoginProps): React.ReactElement => {
    const [searchParams] = useSearchParams();
    const [ac, setAc] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [hash, setHash] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [codeSent, setCodeSent] = useState<string | false>(false);
    const [email, setEmail] = useState<string>(``);
    const [name, setName] = useState<Record<string, string>>({});
    const [rwCode, setRwCode] = useState<string>(``);
    const [expiredTimeout, setExpiredTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [code, setCode] = useState<string>(``);
    const [permDisable, setPermDisable] = useState<Record<string, number>>({});
    const [shrink, setShrink] = useState<boolean>(false);
    const [sp] = useState<string | null>(sessionStorage.getItem(`sp`) ?? searchParams.get(`sp`));
    const location = useLocation();
    const theme = useTheme();
    const navigate = useNavigate();

    const resetAll = (): void => {
        setError(null);
        setLoading(false);
        setCodeSent(false);
        setName({});
        setCode(``);

        if (expiredTimeout) clearTimeout(expiredTimeout);
        setExpiredTimeout(null);
    };

    const send = (): void => {
        if (expiredTimeout) clearTimeout(expiredTimeout);
        setExpiredTimeout(null);
        (codeSent ? codeValidationSchema : validationSchemaLogIn)
            .validate(codeSent ? code : { ...name, email, rwCode })
            .then(() => {
                setError(null);
                setLoading(true);
                return grecaptcha
                    .execute(`6LesMmYbAAAAACONR1kp2flqwlel9cK6gJXDvbkx`, { action: `login` })
                    .then((recaptchaToken: string) =>
                        BareAxios.post(`${baseApi}signin`, {
                            recaptchaToken,
                            programGuid: context.programGuid,
                            ...(!codeSent ? { email, name, rwCode, hash, sp } : { data: codeSent, sp }),
                            code,
                        }).then(r => {
                            if (!codeSent) {
                                setCodeSent(r.data.code);
                                setCode(``);
                                setLoading(false);
                                setName({});
                                setShrink(false);
                                if (r.data.ac) {
                                    setAc(true);
                                }
                                setExpiredTimeout(
                                    setTimeout(() => {
                                        resetAll();
                                        setError(`Login Expired. Please try again.`);
                                        sessionStorage.removeItem(`loginAttempt`);
                                    }, FIFTEEN_MINUTES)
                                );
                                sessionStorage.setItem(
                                    `loginAttempt`,
                                    base91encode(
                                        JSON.stringify({
                                            ac: r.data.ac ? false : ac,
                                            error,
                                            hash,
                                            codeSent: r.data.code,
                                            email,
                                            name: {},
                                            rwCode,
                                            time: Date.now(),
                                            errors,
                                            code: ``,
                                            permDisable,
                                            shrink: false,
                                        })
                                    )
                                );
                            } else {
                                hideGrecaptcha();
                                localStorage.setItem(`sid`, r.data.sid);
                                sessionStorage.removeItem(`loginAttempt`);
                                setLoading(false);
                                navigate(ac ? `/account/payouts` : `/getmygift${location.search}`);
                            }
                        })
                    );
            })
            .catch(e => {
                console.error(e);
                setError(e?.errors?.[0] || e?.response?.data || `Sorry, something went wrong. Please try again.`);
                setLoading(false);
            });
    };

    useEffect(() => {
        resetAll();
    }, [location.pathname]);

    useEffect(() => {
        if (location.state?.reason === `expired`) {
            setError(`Login Expired. Please try again.`);
        }
    }, [location.state]);

    useEffect(() => {
        mountGrecaptcha();
        const auto = searchParams.get(`auto`);
        setHash(searchParams.get(`h`));

        if (auto) {
            setShrink(true);
            const autoFill = JSON.parse(atob(auto)) as Record<string, string>;
            const nm: Record<string, string> = {};
            const disable: Record<string, number> = {};
            if (autoFill.f) {
                nm.firstName = autoFill.f;
                disable.firstName = 1;
            }
            if (autoFill.l) {
                nm.lastName = autoFill.l;
                disable.lastName = 1;
            }
            if (autoFill.r) {
                setRwCode(autoFill.r);
                disable.rwCode = 1;
            }
            setName(nm);
            setPermDisable(disable);
        }
        if (sessionStorage.getItem(`loginAttempt`)) {
            let loginAttempt: Record<string, any> | undefined;
            try {
                loginAttempt = JSON.parse(base91decode(sessionStorage.getItem(`loginAttempt`) as string));
            } catch (e) {
                sessionStorage.removeItem(`loginAttempt`);
            }
            if (!loginAttempt || loginAttempt.time + FIFTEEN_MINUTES < Date.now()) {
                sessionStorage.removeItem(`loginAttempt`); // too old
            } else {
                setAc(loginAttempt.ac);
                setError(loginAttempt.error);
                setHash(loginAttempt.hash);
                setCodeSent(loginAttempt.codeSent);
                setEmail(loginAttempt.email);
                setName(loginAttempt.name);
                setRwCode(loginAttempt.rwCode);
                setExpiredTimeout(
                    setTimeout(
                        () => {
                            resetAll();
                            setError(`Login Expired. Please try again.`);
                        },
                        FIFTEEN_MINUTES - (Date.now() - loginAttempt.time)
                    )
                );
                setErrors(loginAttempt.errors);
                setCode(loginAttempt.code);
                setPermDisable(loginAttempt.permDisable);
                setShrink(loginAttempt.shrink);
            }
        }
        return () => hideGrecaptcha();
    }, []);

    const raiseLabels = (): void => {
        setShrink(true);
    };

    const lowerLabels = (): void => {
        if (onlyEmptyKeys(name) && (!email || codeSent) && !rwCode) {
            setShrink(false);
        }
    };

    return (
        <SignInCard topSmall={!!codeSent} size={size} wide variant="outlined">
            {codeSent && (
                <GreyLine>
                    {svgGradient(theme)}
                    <CheckCircleOutlineOutlined
                        fill="multiStopGradient"
                        sx={{
                            height: 23,
                            width: 23,
                            alignSelf: `center`,
                            fill: `url(#multiStopGradient)`,
                        }}
                    />
                    <Span sx={{ margin: `2px 0 0 5px`, fontSize: 14 }}>Email was sent to {email}</Span>
                </GreyLine>
            )}
            <Typography
                component="h1"
                variant="h4"
                sx={{
                    fontFamily: `Plus Jakarta Sans`,
                    width: `100%`,
                    fontSize: size === `small` ? `28px` : `36px`,
                    letterSpacing: `0.01em`,
                    fontWeight: 700,
                    textAlign: `center`,
                }}
            >
                {codeSent ? `Check your email` : `Let's get your gift`}
            </Typography>
            <Box
                sx={{
                    display: `flex`,
                    flexDirection: `column`,
                    width: `100%`,
                    marginTop: `0`,
                    gap: 2,
                }}
            >
                {!codeSent && (
                    <Typography
                        sx={{
                            textAlign: `center`,
                            fontSize: 18,
                            color: `#4a4a4a`,
                            textWrap: `balance`,
                            marginTop: `8px`,
                            marginBottom: `24px`,
                        }}
                    >
                        Enter your info and the reward code from the letter you received to get your gift.
                    </Typography>
                )}

                {codeSent && (
                    <Box
                        sx={{
                            display: `flex`,
                            flexDirection: `column`,
                            width: `100%`,
                            gap: 1,
                            marginTop: 1,
                            marginBottom: 3,
                        }}
                    >
                        <FormHelperText
                            sx={{
                                mt: 0,
                                fontSize: 18,
                                textAlign: `center`,
                                minHeight: `22px`,
                                minWidth: `2px`,
                                fontWeight: 500,
                                color: `#4a4a4a`,
                                textWrap: `balance`,
                            }}
                        >
                            We&apos;ve sent you a quick verification link. Click that link or enter the code you are
                            provided to verify your email.
                        </FormHelperText>
                        <Box style={{ display: `flex`, justifyContent: `center`, margin: `20px 0 30px` }}>
                            <OTPInput onEnter={send} separator=" " onChange={setCode} value={code} />
                        </Box>
                    </Box>
                )}
                {!codeSent && (
                    <FormControl>
                        <Box sx={{ display: `flex`, justifyContent: `space-between`, mb: 1.5 }}>
                            {(fieldRules.name.fields as FieldDescriptor[]).map(e => (
                                <LoginInputBox
                                    key={e.htmlName}
                                    name={e.htmlName}
                                    InputLabelProps={{ shrink }}
                                    type={e.t}
                                    autoComplete={e.ac}
                                    onFocus={raiseLabels}
                                    onBlur={lowerLabels}
                                    required
                                    label={e.l}
                                    id={e.htmlName}
                                    disabled={loading || !!e.disabled || !!permDisable[e.v]}
                                    onClick={() => setName({ ...name, [e.v]: `` })}
                                    fullWidth={!!codeSent}
                                    InputProps={{
                                        sx: {
                                            marginTop: `5px`,
                                            fontFamily: `Lato`,
                                        },
                                    }}
                                    sx={{
                                        "& label": {
                                            top: 0,
                                            "&.MuiInputLabel-shrink": {
                                                top: `6px`,
                                            },
                                        },
                                        mb: 0,
                                        maxWidth: `unset`,
                                        width: codeSent ? `unset` : `49%`,
                                        ...(e.hidden ? { display: `none` } : {}),
                                        input: {
                                            fontWeight: 500,
                                            paddingRight: `25px`,
                                        },
                                    }}
                                    currentValue={name[e.v]}
                                    helperText={errors?.[e.v]}
                                    error={!!errors?.[e.v]}
                                    onChange={(v: React.ChangeEvent<HTMLInputElement>) => {
                                        setName({
                                            ...name,
                                            [e.v]: v.target.value,
                                        });
                                        setErrors({});
                                        setError(null);
                                    }}
                                />
                            ))}
                        </Box>
                        <LoginInputBox
                            id="email"
                            type="email"
                            disabled={loading}
                            name="email"
                            onFocus={raiseLabels}
                            onBlur={lowerLabels}
                            InputLabelProps={{ shrink }}
                            label="Email"
                            autoComplete="email"
                            onKeyDown={(e: React.KeyboardEvent) => enterInput(e, send)}
                            onChange={(v: React.ChangeEvent<HTMLInputElement>) => {
                                setEmail(v.target.value);
                                setError(null);
                            }}
                            required
                            fullWidth
                            variant="outlined"
                            onClick={() => setEmail(``)}
                            InputProps={{
                                sx: { marginTop: `5px`, fontFamily: `Lato` },
                            }}
                            sx={{
                                marginBottom: 2,
                                "& label": {
                                    top: 0,
                                    "&.MuiInputLabel-shrink": {
                                        top: `6px`,
                                    },
                                },

                                maxWidth: `unset`,
                                input: { fontWeight: 500 },
                            }}
                            currentValue={codeSent ? code : email}
                        />
                        <LoginInputBox
                            id="rewardcode"
                            type="text"
                            disabled={loading || !!permDisable.rwCode}
                            name="rewardcode"
                            onFocus={raiseLabels}
                            onBlur={lowerLabels}
                            InputLabelProps={{ shrink }}
                            label="Reward Code"
                            onKeyDown={(e: React.KeyboardEvent) => enterInput(e, send)}
                            onChange={(v: React.ChangeEvent<HTMLInputElement>) => {
                                setRwCode(v.target.value);
                                setError(null);
                            }}
                            required
                            fullWidth
                            variant="outlined"
                            onClick={() => setRwCode(``)}
                            InputProps={{
                                sx: {
                                    marginTop: `5px`,
                                    fontFamily: `Lato`,
                                },
                            }}
                            sx={{
                                "& label": {
                                    top: 0,
                                    "&.MuiInputLabel-shrink": {
                                        top: `6px`,
                                    },
                                },

                                maxWidth: `unset`,
                                input: { fontWeight: 500 },
                            }}
                            currentValue={rwCode}
                        />
                    </FormControl>
                )}
                {!!error && (
                    <FormHelperText
                        error
                        sx={{
                            fontSize: 13,
                            fontWeight: 500,
                            minHeight: `22px`,
                            minWidth: `2px`,
                            textAlign: `center`,
                        }}
                    >
                        {error}
                    </FormHelperText>
                )}
                {!loading && (
                    <LoginButton
                        sx={{
                            background: `linear-gradient(1deg, #007238, #00a651)`,
                            borderRadius: `50px`,
                            width: `100%`,
                            margin: `0 auto`,
                        }}
                        variant="contained"
                        onClick={send}
                    >
                        {codeSent ? `Continue` : `Get My Gift`}
                    </LoginButton>
                )}
                {loading && (
                    <AppLoadingButton
                        sx={{
                            background: `linear-gradient(1deg, #007238, #00a651)`,
                            borderRadius: `50px`,
                            width: `100%`,
                            margin: `0 auto`,
                        }}
                        spinnerColor="#FFF"
                        loading
                        variant="contained"
                    >
                        {codeSent ? `Continue` : `Get My Gift`}
                    </AppLoadingButton>
                )}

                {!codeSent && (
                    <Typography sx={{ textAlign: `left`, fontSize: `13px` }}>
                        To deliver your rewards, we’ll set up a Cashback Rewards account for you where you can securely
                        access your rewards at any time. By continuing, you agree to our&nbsp;
                        <span>
                            <LiLink
                                to={context?.adminConfig?.mall?.Email_Settings?.Terms_URL ?? ``}
                                target="_blank"
                                variant="body2"
                                sx={{ alignSelf: `center` }}
                            >
                                Terms
                            </LiLink>
                            &nbsp;
                        </span>
                        and acknowledge our&nbsp;
                        <span>
                            <LiLink
                                to={context?.adminConfig?.mall?.Email_Settings?.Privacy_URL ?? ``}
                                target="_blank"
                                variant="body2"
                                sx={{ alignSelf: `center` }}
                            >
                                Privacy Policy
                            </LiLink>
                        </span>
                    </Typography>
                )}
            </Box>
        </SignInCard>
    );
};

export default GetMyGiftLogin;
